import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Badge, Spinner, Alert, Modal, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { useAuthContext } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const AdminEntidadesPage = () => {
  const [entidades, setEntidades] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [impersonatingEntidadeId, setImpersonatingEntidadeId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEntidade, setSelectedEntidade] = useState(null);

  const { startImpersonation } = useAuthContext();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchEntidades = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get('/api/admin/entidades');
        setEntidades(response.data.entidades || []);
        setError(null);
      } catch (err) {
        setError('Não foi possível carregar a lista de clínicas.');
        console.error("Erro ao buscar entidades:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEntidades();
  }, []);

  const handleImpersonate = async (entidade) => {
    setImpersonatingEntidadeId(entidade.id);
    try {
      const response = await axios.post(`/api/admin/entidades/${entidade.id}/impersonate`);
      await startImpersonation(response.data);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erro ao tentar personificar a clínica.';
      alert(errorMessage);
      console.error(error);
      setImpersonatingEntidadeId(null);
    }
  };

  const handleShowDetails = (entidade) => {
    setSelectedEntidade(entidade);
    setShowDetailsModal(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Ativa': return 'success';
      case 'Inadimplente': return 'warning';
      case 'Cancelada': return 'danger';
      case 'Trial': return 'info';
      case 'Inativa': return 'secondary';
      case 'Inativo': return 'danger';
      default: return 'secondary';
    }
  };

  return (
    <Container fluid>
      <h1 className="mb-4">Gerenciamento de Clínicas</h1>
      <Card>
        <Card.Header>
          Lista de Clínicas Cadastradas
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Carregando...</span>
              </Spinner>
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#ID</th>
                  <th>Nome da Clínica</th>
                  <th>CNPJ</th>
                  <th className="text-center">Status da Assinatura</th>
                  <th className="text-center">Status</th>
                  <th>Data de Cadastro</th>
                  <th className="text-center">Corpo Clínico</th>
                  <th className="text-center">Pacientes Ativos</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {entidades.map(entidade => (
                  <tr key={entidade.id}>
                    <td>{entidade.id}</td>
                    <td>{entidade.nome}</td>
                    <td>{entidade.cnpj}</td>
                    <td className="text-center">
                      <Badge bg={getStatusBadge(entidade.statusAssinatura)}>
                        {entidade.statusAssinatura}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Badge pill bg={getStatusBadge(entidade.status)} style={{ fontSize: '0.9em' }}>
                        &nbsp;
                      </Badge>
                    </td>
                    <td>{new Date(entidade.dataCadastro + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                    <td className="text-center">{entidade.totalCorpoClinico}</td>
                    <td className="text-center">{entidade.totalPacientesAtivos}</td>
                    <td className="text-center">
                      <Button variant="outline-info" size="sm" className="me-2" title="Ver Detalhes" onClick={() => handleShowDetails(entidade)}>
                        Ver
                      </Button>
                      <Button 
                        variant="outline-info" 
                        size="sm" 
                        title="Personificar (logar como Representante Legal desta clínica)"
                        onClick={() => handleImpersonate(entidade)}
                        disabled={impersonatingEntidadeId === entidade.id}
                      >
                        {impersonatingEntidadeId === entidade.id ? (
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        ) : (
                          'Personificar'
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal de Detalhes da Clínica */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalhes da Clínica</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEntidade && (
            <Row>
              <Col md={6}>
                <p><strong>ID:</strong> {selectedEntidade.id}</p>
                <p><strong>Nome:</strong> {selectedEntidade.nome}</p>
                <p><strong>CNPJ:</strong> {selectedEntidade.cnpj}</p>
                <p><strong>Data de Cadastro:</strong> {new Date(selectedEntidade.dataCadastro + 'T00:00:00').toLocaleDateString('pt-BR')}</p>
              </Col>
              <Col md={6}>
                <p><strong>Status da Clínica:</strong> <Badge bg={getStatusBadge(selectedEntidade.status)}>{selectedEntidade.status}</Badge></p>
                <p><strong>Status da Assinatura:</strong> <Badge bg={getStatusBadge(selectedEntidade.statusAssinatura)}>{selectedEntidade.statusAssinatura}</Badge></p>
                <p><strong>Total Corpo Clínico:</strong> {selectedEntidade.totalCorpoClinico}</p>
                <p><strong>Total Pacientes Ativos:</strong> {selectedEntidade.totalPacientesAtivos}</p>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Fechar
          </Button>
          <Button variant="primary" onClick={() => alert('Função de edição a ser implementada.')}>
            Editar Clínica
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default AdminEntidadesPage;